import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Users,
  UserPlus,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  Printer,
  Trash2,
  Loader2,
  CheckCircle,
  AlertCircle,
  XCircle,
  Circle,
  Search,
  Plus,
  Building2,
  Cake,
  Phone,
  Mail,
  ArrowDownUp,
  ArrowUpAZ,
  ArrowDownAZ,
  Trophy,
} from "lucide-react";
import { patientApi } from "../services/api";
import TabelFotbalDialog from "../components/TabelFotbalDialog";
import { toast } from "sonner";

// Culori concluzii — sincronizat cu ReferatMedicalButton.jsx
const CONCLUZIE_META = {
  APT: { label: "APT", cls: "bg-emerald-100 text-emerald-800 border-emerald-500", Icon: CheckCircle, printColor: "#059669" },
  APT_SUPRAVEGHERE: { label: "APT SUB SUPRAVEGHERE", cls: "bg-amber-100 text-amber-800 border-amber-500", Icon: AlertCircle, printColor: "#d97706" },
  APT_INDIVIDUALIZAT: { label: "APT INDIVIDUALIZAT", cls: "bg-orange-100 text-orange-800 border-orange-500", Icon: AlertCircle, printColor: "#ea580c" },
  APT_CONDITIONAT: { label: "APT CONDIȚIONAT", cls: "bg-orange-100 text-orange-800 border-orange-500", Icon: AlertCircle, printColor: "#f97316" },
  INAPT_TEMPORAR: { label: "INAPT TEMPORAR", cls: "bg-red-100 text-red-800 border-red-500", Icon: XCircle, printColor: "#ef4444" },
  INAPT: { label: "INAPT", cls: "bg-slate-900 text-white border-slate-900", Icon: XCircle, printColor: "#0f172a" },
};

const ConcluzieBadge = ({ value }) => {
  if (!value) {
    return <Badge variant="outline" className="text-xs text-muted-foreground"><Circle className="w-3 h-3 mr-1" />Necompletat</Badge>;
  }
  const meta = CONCLUZIE_META[value];
  if (!meta) return <Badge variant="outline">{value}</Badge>;
  const { Icon } = meta;
  return (
    <Badge className={`text-xs border ${meta.cls}`}>
      <Icon className="w-3 h-3 mr-1" />
      {meta.label}
    </Badge>
  );
};

// Format ISO datetime la DD.MM.YYYY (data înregistrării prin formular).
const fmtInregistrare = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  } catch {
    return "";
  }
};

// Comparator ascendent după created_at (cronologic: vechi → nou).
const byCreatedAsc = (a, b) => {
  const av = a?.created_at || "";
  const bv = b?.created_at || "";
  if (av === bv) return 0;
  return av < bv ? -1 : 1;
};

// Filtrare listă sportivi după interval (toți / anul curent / ultimele 30 zile).
const filterByRange = (list, range) => {
  if (!list || range === "all") return list || [];
  const now = new Date();
  if (range === "year") {
    const y = now.getFullYear();
    return list.filter((p) => {
      const c = p?.created_at || "";
      return c.startsWith(String(y));
    });
  }
  if (range === "30d") {
    const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30).toISOString();
    return list.filter((p) => (p?.created_at || "") >= cutoff);
  }
  return list;
};

// Sortare aplicabilă (asc / desc) pe copia listei.
const applySort = (list, desc) => {
  const arr = (list || []).slice().sort(byCreatedAsc);
  return desc ? arr.reverse() : arr;
};

// Helper: construiește HTML-ul unui aviz medico-sportiv complet pentru print/PDF
const buildAvizHTML = (patient) => {
  const dp = patient?.date_personale || {};
  const is = patient?.info_sportive || {};
  const a1 = patient?.atelier_1 || {};
  const a2 = patient?.atelier_2 || {};
  const a5 = patient?.atelier_5 || {};
  const referat = patient?.referat || {};
  const nume = `${(dp.nume || "").toUpperCase()} ${(dp.prenume || "").toUpperCase()}`.trim();
  const sport = is.sport_practicat || "";
  const nrViza = patient?.numar_viza || patient?.numar_ordine || "";
  const today = new Date();
  const fmt = (d) => `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  const dataCircuit = fmt(today);
  const dataValabilPana = fmt(new Date(today.getFullYear(), today.getMonth() + 6, today.getDate()));

  const diagParts = [];
  const nz = (v) => v && String(v).trim() !== "" && String(v) !== "0";
  const dez = [];
  if (nz(a1.talie)) dez.push(`Talia ${a1.talie} cm`);
  if (nz(a1.greutate)) dez.push(`greutate ${a1.greutate} kg`);
  if (nz(a1.imc)) dez.push(`IMC ${a1.imc}`);
  if (dez.length) diagParts.push(`Dezvoltare fizică: ${dez.join("; ")}.`);
  const func = [];
  if (nz(a2.ruffier_index)) func.push(`Ruffier ${a2.ruffier_index}`);
  if (nz(a2.ruffier_interpretare)) func.push(a2.ruffier_interpretare);
  if (func.length) diagParts.push(`Capacitate de efort: ${func.join("; ")}.`);
  if (nz(a5.concluzii)) diagParts.push(`Analize: ${a5.concluzii}`);

  const recs = [];
  const pushRaw = (s) => nz(s) && recs.push(String(s).trim());
  (referat.recomandari || []).forEach((r) => nz(r?.text) && recs.push(r.text.trim()));
  pushRaw(a1.recomandari_posturale);
  pushRaw(a1.recomandare_nutritie);
  pushRaw(a1.recomandare_sport);
  pushRaw(a2.astrand_revenire_recomandare);
  pushRaw(a5.recomandari);
  recs.push("Antrenamente cu dozare gradată, conform etapei de pregătire și toleranței individuale cu accent initial pe pregătire fizică generală și pregătire specifica, cu încălzire atentă.");
  recs.push("Refacere complexa post efort.");
  const uniqueRecs = [...new Set(recs.map((s) => s.trim()).filter(Boolean))];

  const concluzieVal = referat.concluzie || "";
  const concluzieMeta = CONCLUZIE_META[concluzieVal];
  const concluzieHtml = concluzieMeta
    ? `<div class="concluzie" style="border:3px solid ${concluzieMeta.printColor};background:${concluzieMeta.printColor}20;color:${concluzieMeta.printColor};padding:8px 12px;margin:10px 0;text-align:center;font-weight:bold;font-size:14pt;letter-spacing:1px;text-transform:uppercase;">${concluzieMeta.label}</div>`
    : "";

  return `
<article class="aviz-page">
  <div class="aviz-header">
    <h1>CABINET MEDICINĂ SPORTIVĂ</h1>
    <h2>CONCLUZII AVIZ MEDICO-SPORTIV</h2>
    <p>(nr. ${nrViza} · ${dataCircuit})</p>
  </div>
  <div class="kv">
    <span class="k">Nume sportiv:</span><span>${nume || "—"}</span>
    <span class="k">Sportul practicat:</span><span>${sport || "—"}</span>
    <span class="k">Număr fișă:</span><span>${nrViza}</span>
    <span class="k">Data circuitului:</span><span>${dataCircuit}</span>
  </div>
  ${concluzieHtml}
  <div class="sec"><h3>DIAGNOSTIC</h3><p>${diagParts.join("<br/>") || "—"}</p></div>
  <div class="sec"><h3>SELECȚIE</h3><p>Apt ${sport || "sport"} - biotip perfectibil morfo-funcțional.</p></div>
  <div class="sec"><h3>RECOMANDĂRI</h3><ol>${uniqueRecs.map((r) => `<li>${r}</li>`).join("")}</ol></div>
  <div class="apt-line">APT ANTRENAMENTE ȘI COMPETIȚII</div>
  <div class="valid"><span><strong>Valabil de la:</strong> ${dataCircuit}</span><span><strong>Până la:</strong> ${dataValabilPana}</span></div>
  <div class="footer">
    <div class="col"><strong>DIRECTOR / MEDIC PRIMAR</strong><div class="sig-line"></div><div class="small">(semnătură / parafă)</div></div>
    <div class="col"><strong>ȘTAMPILA</strong><div class="sig-line"></div></div>
  </div>
  <div class="disclaimer">Modificarea stării de sănătate poate invalida prezentul aviz medico-sportiv.</div>
</article>`;
};

// Dialog pentru adăugare manuală sportiv la un antrenor
function AddAthleteDialog({ open, onOpenChange, coach, currentAthleteIds, onAdded }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    if (!open) { setSearch(""); setResults([]); return; }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const list = await patientApi.searchPatients(q);
        const sorted = (list || []).slice().sort(byCreatedAsc);
        setResults(sorted);
      } catch (e) {
        toast.error("Eroare la căutare.");
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search, open]);

  const handleAdd = async (patient) => {
    setAdding(patient.id);
    try {
      await patientApi.addAthleteToCoach(coach.id, patient.id);
      toast.success(`${patient.date_personale?.nume} ${patient.date_personale?.prenume} adăugat.`);
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la adăugare.");
    } finally {
      setAdding(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="add-athlete-dialog">
        <DialogHeader>
          <DialogTitle>Adaugă sportiv la {coach?.name}</DialogTitle>
          <DialogDescription>
            Caută după nume, prenume sau club. Poți adăuga orice sportiv — chiar dacă
            nu corespunde automat criteriilor (nume antrenor + club + an naștere).
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Caută sportiv (min. 2 caractere)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="athlete-search-input"
            autoFocus
          />
        </div>
        <div className="max-h-80 overflow-y-auto border rounded-md divide-y">
          {loading && (
            <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Se caută...
            </div>
          )}
          {!loading && search.trim().length >= 2 && results.length === 0 && (
            <div className="p-3 text-sm text-muted-foreground italic">Niciun rezultat.</div>
          )}
          {!loading && search.trim().length < 2 && (
            <div className="p-3 text-sm text-muted-foreground italic">
              Începe să scrii pentru a căuta sportivi.
            </div>
          )}
          {!loading && results.map((p) => {
            const already = currentAthleteIds.has(p.id);
            const inreg = fmtInregistrare(p.created_at);
            return (
              <div key={p.id} className="p-2.5 flex items-center gap-3 text-sm" data-testid={`search-result-${p.id}`}>
                <span className="text-muted-foreground w-10 shrink-0 text-xs">#{p.numar_ordine}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {p.date_personale?.nume} {p.date_personale?.prenume}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {p.info_sportive?.sport_practicat || "—"} · {p.info_sportive?.club_sportiv || "—"}
                    {p.info_sportive?.antrenor ? ` · Antrenor: ${p.info_sportive.antrenor}` : ""}
                    {p.date_personale?.data_nasterii ? ` · N: ${p.date_personale.data_nasterii}` : ""}
                  </div>
                </div>
                {inreg && (
                  <span
                    className="text-[11px] text-muted-foreground shrink-0 tabular-nums"
                    title="Data înregistrării prin formular"
                    data-testid={`search-created-${p.id}`}
                  >
                    {inreg}
                  </span>
                )}
                {already ? (
                  <Badge variant="secondary" className="text-xs">Deja inclus</Badge>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdd(p)}
                    disabled={adding === p.id}
                    data-testid={`add-athlete-${p.id}`}
                  >
                    {adding === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                    Adaugă
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RaportAntrenori() {
  const [loading, setLoading] = useState(true);
  const [coaches, setCoaches] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [athletesMap, setAthletesMap] = useState({});   // { [coachId]: [athlete,...] }
  const [selected, setSelected] = useState({});         // { [patientId]: true }
  const [exporting, setExporting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addAthleteFor, setAddAthleteFor] = useState(null); // coach obj
  const [form, setForm] = useState({ name: "", club: "", grupa: "", telefon: "", email: "" });
  const [dateFilter, setDateFilter] = useState("all"); // "all" | "year" | "30d"
  const [sortDesc, setSortDesc] = useState(false);      // false = cronologic (vechi → nou)
  // Tabel fotbal per antrenor
  const [tabelFotbalFor, setTabelFotbalFor] = useState(null); // { coach, athletes } | null

  const fetchCoaches = useCallback(async () => {
    setLoading(true);
    try {
      const list = await patientApi.listCoaches();
      setCoaches(list);
    } catch (e) {
      toast.error("Nu am putut încărca lista de antrenori.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCoaches(); }, [fetchCoaches]);

  const loadAthletes = useCallback(async (coachId) => {
    try {
      const list = await patientApi.coachAthletes(coachId);
      setAthletesMap((p) => ({ ...p, [coachId]: list }));
    } catch (e) {
      toast.error("Nu am putut încărca sportivii.");
    }
  }, []);

  const toggleExpand = async (coach) => {
    const isOpen = !!expanded[coach.id];
    setExpanded((p) => ({ ...p, [coach.id]: !isOpen }));
    if (!isOpen && !athletesMap[coach.id]) {
      await loadAthletes(coach.id);
    }
  };

  const handleAddCoach = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Numele antrenorului este obligatoriu."); return; }
    try {
      await patientApi.createCoach({
        name,
        club: form.club.trim(),
        grupa: form.grupa.trim(),
        telefon: form.telefon.trim(),
        email: form.email.trim(),
      });
      toast.success(`Antrenorul „${name}” a fost adăugat.`);
      setForm({ name: "", club: "", grupa: "", telefon: "", email: "" });
      setAddOpen(false);
      await fetchCoaches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la adăugare.");
    }
  };

  const handleDeleteCoach = async (coach) => {
    if (!window.confirm(`Șterge antrenorul „${coach.name}” din listă?\n(Sportivii asociați rămân neschimbați în DB.)`)) return;
    try {
      await patientApi.deleteCoach(coach.id);
      toast.success("Șters.");
      await fetchCoaches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere.");
    }
  };

  const handleRemoveAthlete = async (coach, patient) => {
    if (!window.confirm(`Elimină „${patient.date_personale?.nume} ${patient.date_personale?.prenume}” din raportul antrenorului?`)) return;
    try {
      await patientApi.removeAthleteFromCoach(coach.id, patient.id);
      toast.success("Eliminat.");
      await loadAthletes(coach.id);
      await fetchCoaches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la eliminare.");
    }
  };

  const toggleSelected = (pid) => setSelected((p) => ({ ...p, [pid]: !p[pid] }));

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const handleExport = async () => {
    const selectedIds = Object.keys(selected).filter((k) => selected[k]);
    if (!selectedIds.length) {
      toast.info("Selectează cel puțin un sportiv.");
      return;
    }
    setExporting(true);
    try {
      const fullPatients = await Promise.all(selectedIds.map((id) => patientApi.getPatient(id)));
      const html = fullPatients.map(buildAvizHTML).join('');
      const styles = `
        @page { size: A4; margin: 14mm; }
        body { font-family: 'Times New Roman', Georgia, serif; color: #111; margin: 0; padding: 0; font-size: 11pt; line-height: 1.35; }
        .aviz-page { page-break-after: always; }
        .aviz-page:last-child { page-break-after: auto; }
        .page-break { page-break-after: always; }
        .aviz-header { text-align: center; border-bottom: 2px solid #111; padding-bottom: 6px; margin-bottom: 10px; }
        .aviz-header h1 { font-size: 13pt; font-weight: bold; margin: 0; text-transform: uppercase; letter-spacing: 0.5px; }
        .aviz-header h2 { font-size: 12pt; font-weight: bold; margin: 4px 0 0 0; text-transform: uppercase; }
        .aviz-header p { font-size: 9pt; margin: 4px 0 0 0; font-style: italic; }
        .kv { display: grid; grid-template-columns: max-content 1fr; column-gap: 10px; row-gap: 4px; margin-bottom: 10px; font-size: 10pt; }
        .kv .k { font-weight: bold; }
        .sec { margin: 8px 0; }
        .sec h3 { font-size: 11pt; font-weight: bold; text-transform: uppercase; margin: 0 0 4px 0; letter-spacing: 0.5px; }
        .sec p { margin: 0; white-space: pre-line; text-align: justify; }
        .sec ol { margin: 0; padding-left: 22px; }
        .sec ol li { margin: 2px 0; }
        .apt-line { text-align: center; font-weight: bold; font-size: 12pt; margin: 10px 0; text-transform: uppercase; letter-spacing: 1px; }
        .valid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 10pt; margin-bottom: 20px; }
        .footer { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 30px; }
        .footer .col { text-align: center; font-size: 10pt; }
        .sig-line { border-top: 1px solid #111; margin: 40px 20px 4px; }
        .small { font-size: 9pt; }
        .disclaimer { margin-top: 20px; font-size: 9pt; font-style: italic; text-align: center; border-top: 1px dashed #999; padding-top: 6px; }
        .concluzie { font-family: 'Arial Black', sans-serif; }
      `;
      const doc = `<!DOCTYPE html><html lang="ro"><head><meta charset="utf-8"><title>Raport avize sportivi</title><style>${styles}</style></head><body>${html}</body></html>`;
      const w = window.open("", "_blank", "width=900,height=1000");
      if (!w) {
        toast.error("Pop-up blocat — permite pop-up-urile pentru această pagină.");
        return;
      }
      w.document.open();
      w.document.write(doc);
      w.document.close();
      w.onload = () => setTimeout(() => { w.focus(); w.print(); }, 500);
      toast.success(`Export inițiat: ${selectedIds.length} sportivi.`);
    } catch (e) {
      console.error(e);
      toast.error("Eroare la export.");
    } finally {
      setExporting(false);
    }
  };

  const currentAthleteIds = useMemo(() => {
    if (!addAthleteFor) return new Set();
    return new Set((athletesMap[addAthleteFor.id] || []).map((a) => a.id));
  }, [addAthleteFor, athletesMap]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="raport-antrenori-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-2 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/admin/dashboard">
            <Button variant="outline" size="sm" className="gap-1" data-testid="back-dashboard">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: "'Work Sans', sans-serif" }}>
              <Users className="w-6 h-6 text-primary" />
              Raport Antrenori
            </h1>
            <p className="text-sm text-muted-foreground">
              Antrenori manuali (Nume + Club + Grupa). Sportivii apar automat dacă se potrivesc după toate 3 criteriile; poți adăuga/elimina manual oricare sportiv.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="add-coach-btn">
                <UserPlus className="w-4 h-4" /> Adaugă antrenor
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-coach-modal">
              <DialogHeader>
                <DialogTitle>Adaugă antrenor nou</DialogTitle>
                <DialogDescription>
                  Sportivii care au aceleași <b>Nume antrenor</b>, <b>Club</b> și an de naștere = <b>Grupa</b> vor apărea automat în listă.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="coach-name">Nume antrenor *</Label>
                  <Input
                    id="coach-name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="ex: Popescu Ion"
                    data-testid="new-coach-name"
                    onKeyDown={(e) => e.key === "Enter" && form.name.trim() && handleAddCoach()}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="coach-club">Clubul</Label>
                  <Input
                    id="coach-club"
                    value={form.club}
                    onChange={(e) => setForm((f) => ({ ...f, club: e.target.value }))}
                    placeholder="ex: CSA Steaua"
                    data-testid="new-coach-club"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="coach-grupa">Grupa (an naștere)</Label>
                  <Input
                    id="coach-grupa"
                    value={form.grupa}
                    onChange={(e) => setForm((f) => ({ ...f, grupa: e.target.value }))}
                    placeholder="ex: 2010"
                    inputMode="numeric"
                    data-testid="new-coach-grupa"
                  />
                  <p className="text-xs text-muted-foreground">Se compară cu anul din data nașterii sportivilor.</p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="coach-telefon">Telefon</Label>
                  <Input
                    id="coach-telefon"
                    type="tel"
                    value={form.telefon}
                    onChange={(e) => setForm((f) => ({ ...f, telefon: e.target.value }))}
                    placeholder="ex: 0722 123 456"
                    data-testid="new-coach-telefon"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="coach-email">Email</Label>
                  <Input
                    id="coach-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="ex: antrenor@club.ro"
                    data-testid="new-coach-email"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddOpen(false)}>Anulează</Button>
                <Button onClick={handleAddCoach} disabled={!form.name.trim()} data-testid="save-coach-btn">Adaugă</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button
            variant="default"
            className="gap-2"
            onClick={handleExport}
            disabled={selectedCount === 0 || exporting}
            data-testid="export-avize-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
            Exportă {selectedCount > 0 ? `(${selectedCount})` : ""} PDF
          </Button>
        </div>
      </div>

      {/* Filter + Sort controls */}
      {coaches.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-3 text-xs" data-testid="athlete-filter-bar">
          <span className="text-muted-foreground uppercase tracking-wide font-medium">Filtrează sportivii:</span>
          <div className="inline-flex rounded-md border overflow-hidden">
            <button
              type="button"
              className={`px-3 py-1 transition ${dateFilter === "all" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              onClick={() => setDateFilter("all")}
              data-testid="filter-all"
            >
              Toți
            </button>
            <button
              type="button"
              className={`px-3 py-1 border-l transition ${dateFilter === "year" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              onClick={() => setDateFilter("year")}
              data-testid="filter-year"
            >
              Anul curent
            </button>
            <button
              type="button"
              className={`px-3 py-1 border-l transition ${dateFilter === "30d" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              onClick={() => setDateFilter("30d")}
              data-testid="filter-30d"
            >
              Ultimele 30 zile
            </button>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1 h-7 text-xs"
            onClick={() => setSortDesc((v) => !v)}
            data-testid="sort-toggle"
            title={sortDesc ? "Cei mai vechi înregistrați primii" : "Cei mai recenți înregistrați primii"}
          >
            {sortDesc ? <ArrowDownAZ className="w-3.5 h-3.5" /> : <ArrowUpAZ className="w-3.5 h-3.5" />}
            {sortDesc ? "Recenți întâi" : "Vechi întâi (cronologic)"}
            <ArrowDownUp className="w-3 h-3 opacity-60" />
          </Button>
        </div>
      )}

      {/* Coach list */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Se încarcă antrenorii...
        </div>
      ) : coaches.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Niciun antrenor înregistrat. Folosește <b>Adaugă antrenor</b> pentru a adăuga primul.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {coaches.map((coach) => (
            <Card key={coach.id} data-testid={`coach-card-${coach.id}`}>
              <CardHeader className="py-3 cursor-pointer" onClick={() => toggleExpand(coach)}>
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 flex-wrap">
                    {expanded[coach.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <Users className="w-4 h-4 text-primary" />
                    <span className="font-semibold">{coach.name}</span>
                    {coach.club && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> {coach.club}
                      </span>
                    )}
                    {coach.grupa && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Cake className="w-3 h-3" /> {coach.grupa}
                      </span>
                    )}
                    {coach.telefon && (
                      <a
                        href={`tel:${coach.telefon}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-muted-foreground hover:text-slate-800 flex items-center gap-1"
                        data-testid={`coach-telefon-${coach.id}`}
                      >
                        <Phone className="w-3 h-3" /> {coach.telefon}
                      </a>
                    )}
                    {coach.email && (
                      <a
                        href={`mailto:${coach.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-muted-foreground hover:text-slate-800 flex items-center gap-1"
                        data-testid={`coach-email-${coach.id}`}
                      >
                        <Mail className="w-3 h-3" /> {coach.email}
                      </a>
                    )}
                    <Badge variant="secondary" className="ml-1">{coach.patients_count} sportivi</Badge>
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Asigurăm că avem sportivii încărcați
                        let list = athletesMap[coach.id];
                        if (!list) {
                          try {
                            list = await patientApi.coachAthletes(coach.id);
                            setAthletesMap((p) => ({ ...p, [coach.id]: list }));
                          } catch (err) {
                            toast.error("Nu am putut încărca sportivii antrenorului.");
                            return;
                          }
                        }
                        if (!list || list.length === 0) {
                          toast.info("Antrenorul nu are sportivi asociați.");
                          return;
                        }
                        setTabelFotbalFor({ coach, athletes: list });
                      }}
                      title="Generează tabel fotbal pentru sportivii acestui antrenor"
                      data-testid={`tabel-fotbal-coach-${coach.id}`}
                    >
                      <Trophy className="w-4 h-4" />
                      <span className="hidden sm:inline">Tabel fotbal</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={(e) => { e.stopPropagation(); handleDeleteCoach(coach); }}
                      title="Șterge antrenorul din listă"
                      data-testid={`delete-coach-${coach.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              {expanded[coach.id] && (
                <CardContent className="pt-0" data-testid={`coach-athletes-${coach.id}`}>
                  <div className="flex justify-end mb-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => setAddAthleteFor(coach)}
                      data-testid={`add-athlete-btn-${coach.id}`}
                    >
                      <Plus className="w-3 h-3" /> Adaugă sportiv
                    </Button>
                  </div>
                  {!athletesMap[coach.id] ? (
                    <div className="text-xs text-muted-foreground py-2">Se încarcă sportivii...</div>
                  ) : athletesMap[coach.id].length === 0 ? (
                    <div className="text-xs text-muted-foreground py-3 italic text-center border rounded-md">
                      Niciun sportiv asociat. Folosește <b>Adaugă sportiv</b> pentru a adăuga manual.
                    </div>
                  ) : (() => {
                    const filtered = applySort(filterByRange(athletesMap[coach.id], dateFilter), sortDesc);
                    if (filtered.length === 0) {
                      return (
                        <div className="text-xs text-muted-foreground py-3 italic text-center border rounded-md" data-testid={`no-athletes-in-filter-${coach.id}`}>
                          Niciun sportiv în intervalul selectat. Modifică filtrul de sus.
                        </div>
                      );
                    }
                    return (
                    <ul className="divide-y border rounded-md">
                      {filtered.map((p) => {
                        const conc = (p.referat || {}).concluzie || "";
                        // Preferăm data ultimei salvări a Referatului medical; fallback pe created_at.
                        const refDate = (p.referat || {}).saved_at || p.created_at;
                        const inreg = fmtInregistrare(refDate);
                        return (
                          <li key={p.id} className="flex items-center gap-3 py-2 px-2 text-sm" data-testid={`athlete-row-${p.id}`}>
                            <Checkbox
                              checked={!!selected[p.id]}
                              onCheckedChange={() => toggleSelected(p.id)}
                              data-testid={`select-athlete-${p.id}`}
                            />
                            <span className="text-muted-foreground w-10 shrink-0 text-xs">#{p.numar_ordine}</span>
                            <Link to={`/admin/patients/${p.id}`} className="flex-1 truncate hover:underline">
                              <span className="font-medium">{p.date_personale?.nume} {p.date_personale?.prenume}</span>
                              <span className="text-muted-foreground ml-2 text-xs">
                                {p.info_sportive?.sport_practicat} · {p.info_sportive?.club_sportiv}
                                {p.date_personale?.data_nasterii ? ` · N: ${p.date_personale.data_nasterii}` : ""}
                              </span>
                            </Link>
                            {inreg && (
                              <span
                                className="text-[11px] text-muted-foreground shrink-0 tabular-nums"
                                title={(p.referat || {}).saved_at ? "Data ultimei salvări a Referatului medical" : "Data înregistrării prin formular (Referat nesalvat încă)"}
                                data-testid={`athlete-referat-saved-${p.id}`}
                              >
                                {inreg}
                              </span>
                            )}
                            {p.source === "manual" ? (
                              <Badge variant="outline" className="text-[10px] border-blue-500 text-blue-700">manual</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px]">auto</Badge>
                            )}
                            <ConcluzieBadge value={conc} />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive"
                              onClick={() => handleRemoveAthlete(coach, p)}
                              title="Elimină din raport"
                              data-testid={`remove-athlete-${p.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                    );
                  })()}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {addAthleteFor && (
        <AddAthleteDialog
          open={!!addAthleteFor}
          onOpenChange={(v) => { if (!v) setAddAthleteFor(null); }}
          coach={addAthleteFor}
          currentAthleteIds={currentAthleteIds}
          onAdded={async () => {
            await loadAthletes(addAthleteFor.id);
            await fetchCoaches();
          }}
        />
      )}

      {/* Tabel fotbal per antrenor — dialog pre-populat cu sportivii antrenorului */}
      <TabelFotbalDialog
        open={!!tabelFotbalFor}
        onOpenChange={(v) => { if (!v) setTabelFotbalFor(null); }}
        initialAthletes={tabelFotbalFor?.athletes || null}
        contextLabel={tabelFotbalFor ? `Antrenor: ${tabelFotbalFor.coach.name}` : ""}
      />
    </div>
  );
}
